import React, { Component, ErrorInfo, ReactNode } from 'react';
import { FiAlertTriangle, FiRefreshCw, FiSave, FiHome } from 'react-icons/fi';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onRecover?: () => void;
  programId?: string | number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  savedFormData: any;
}

/**
 * Error boundary specifically designed for the ProgramEditor
 * Preserves form state and provides recovery options
 */
class ProgramEditorErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      savedFormData: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Try to preserve form data from localStorage
    let savedFormData = null;
    try {
      const programKey = this.props.programId || 'new';
      const autoSaveData = localStorage.getItem(`program-autosave-${programKey}`);
      const draftData = localStorage.getItem(`program-draft-${programKey}`);
      
      if (autoSaveData) {
        savedFormData = JSON.parse(autoSaveData);
      } else if (draftData) {
        savedFormData = JSON.parse(draftData);
      }
    } catch (e) {
      console.warn('Failed to retrieve saved form data:', e);
    }

    // Log the error
    console.error('ProgramEditor Error Boundary caught error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId,
      hasSavedData: !!savedFormData
    });

    this.setState({
      errorInfo,
      errorId,
      savedFormData
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Auto-reset after 60 seconds to prevent permanent error states
    this.resetTimeoutId = window.setTimeout(() => {
      this.handleReset();
    }, 60000);
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  handleReset = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      savedFormData: null
    });

    this.props.onRecover?.();
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard/programs';
  };

  handleSaveAndReload = () => {
    if (this.state.savedFormData) {
      try {
        const programKey = this.props.programId || 'new';
        localStorage.setItem(`program-recovery-${programKey}`, JSON.stringify({
          ...this.state.savedFormData,
          recoveryTimestamp: Date.now(),
          errorId: this.state.errorId
        }));
        
        // Show success message
        alert('Form data has been saved for recovery. The page will now reload.');
      } catch (e) {
        console.error('Failed to save recovery data:', e);
        alert('Failed to save form data. Please copy any important information before reloading.');
      }
    }
    
    this.handleReload();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorId, savedFormData } = this.state;
      
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                <FiAlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Oops! Something went wrong
              </h1>
              
              <p className="text-gray-600 mb-6">
                The program editor encountered an unexpected error. Don't worry - your work may be recoverable.
              </p>

              {savedFormData && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <FiSave className="h-5 w-5 text-blue-600 mr-2" />
                    <div className="text-left">
                      <h3 className="text-sm font-medium text-blue-800">
                        Form Data Preserved
                      </h3>
                      <p className="text-sm text-blue-600 mt-1">
                        We found auto-saved data from{' '}
                        {savedFormData.timestamp 
                          ? new Date(savedFormData.timestamp).toLocaleString()
                          : 'recently'
                        }. You can recover your work when you reload.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <details className="text-left bg-gray-50 rounded-lg p-4 mb-6">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                    Technical Details (Click to expand)
                  </summary>
                  <div className="text-xs text-gray-600 space-y-2">
                    <div>
                      <strong>Error:</strong> {error.message}
                    </div>
                    {errorId && (
                      <div>
                        <strong>Error ID:</strong> {errorId}
                      </div>
                    )}
                    <div className="max-h-32 overflow-y-auto bg-white p-2 rounded border">
                      <pre className="whitespace-pre-wrap text-xs">
                        {error.stack}
                      </pre>
                    </div>
                  </div>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleReset}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FiRefreshCw className="h-4 w-4" />
                  Try Again
                </button>
                
                {savedFormData ? (
                  <button
                    onClick={this.handleSaveAndReload}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FiSave className="h-4 w-4" />
                    Save & Reload
                  </button>
                ) : (
                  <button
                    onClick={this.handleReload}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <FiRefreshCw className="h-4 w-4" />
                    Reload Page
                  </button>
                )}
                
                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FiHome className="h-4 w-4" />
                  Go to Programs
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-6">
                If this problem persists, please contact support and provide the Error ID above.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ProgramEditorErrorBoundary;
